import React, { useState, useCallback, useRef } from 'react';
import { hoverable } from '../../themes/utils/responsiveHover';
import {
  Alert,
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Typography,
  Skeleton,
  Snackbar,
  Button,
  Popper,
  Fade,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import useIsOverflowing from '../../hooks/useIsOverflowing';
import { useTranslation } from 'react-i18next';
import { useRoles } from '../../hooks/useRoles';
import { useRoleBuilderUpdates } from '../../hooks/useRoleBuilderUpdates';
import { formatRelativeDraft } from '../explore/AgentDraftBadge';
import { useAgentPreferences } from '../../hooks/useAgentPreferences';
import { useSidebar } from '../../context/SidebarContext';
import { useStartAgentChat } from '../../hooks/useStartAgentChat';
import { getInitials } from '../editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import postgresBackendService from '../../services/postgresBackendService';
import SidebarItemMenu from './SidebarItemMenu';
import AgentPreviewCard from '../agents/AgentPreviewCard';
import NewAgentTag from '../agents/NewAgentTag';

const DEFAULT_VISIBLE = 5;
const LS_KEY_COLLAPSED = 'sidebar_agents_collapsed';

/** Inner component so the overflow hook can be called per agent item. */
const AgentNameText = ({ name }) => {
  const [ref, isOverflowing] = useIsOverflowing();
  return (
    <Tooltip title={name} disableHoverListener={!isOverflowing} arrow placement="right" enterDelay={400}>
      <ListItemText
        primary={name}
        primaryTypographyProps={{
          ref,
          variant: 'body2',
          noWrap: true,
          sx: { textOverflow: 'ellipsis' },
        }}
      />
    </Tooltip>
  );
};

/**
 * SidebarAgentsList — Shows user's AI roles/agents in the sidebar.
 * Each agent displays a colored avatar circle + truncated name.
 * Click starts a NEW chat with that agent via setPendingRolId (ChatGPT-style).
 */
const SidebarAgentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { roles, isLoading, refetch, updateOptimistic, removeOptimistic } = useRoles({ includeDraft: true });

  // Keep the sidebar in sync with draft state changes coming from the Builder
  // (N8N writes drafts via WS) or from the chat banner publish/discard flow.
  // Updating in place avoids a refetch flash; refetch falls back as the safety
  // net for inserts/restores where the role isn't in the cache.
  useRoleBuilderUpdates({
    onUpdate: (rolId, changes) => {
      // #759: un agente que aún no está en la lista lo acaba de sumar una plantilla.
      if (!roles.some((r) => r.id === rolId)) {
        refetch();
        return;
      }
      if (changes?.isNew === false) updateOptimistic(rolId, { isNew: false });
      if (changes?.hasDraft === true || changes?.hasDraft === false) {
        updateOptimistic(rolId, {
          hasDraft: changes.hasDraft,
          draftUpdatedAt: changes.draftUpdatedAt || null,
        });
      }
    },
    onInsert: () => { refetch(); },
    onArchive: (rolId) => { removeOptimistic(rolId); },
    onRestore: () => { refetch(); },
  });
  const { hiddenAgentIds, toggleHidden } = useAgentPreferences();
  const { isMobile, closeOverlay } = useSidebar();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [hoveredAgentId, setHoveredAgentId] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem(LS_KEY_COLLAPSED) === 'true');
  const [showAll, setShowAll] = useState(false);
  const [undoSnack, setUndoSnack] = useState({ open: false, role: null });
  const [errorSnack, setErrorSnack] = useState({ open: false, message: '' });
  const undoTimerRef = useRef(null);

  // Hover preview popper — separate from hoveredAgentId (which drives ⋮ opacity instantly)
  const [previewRole, setPreviewRole] = useState(null);
  const [previewAnchorEl, setPreviewAnchorEl] = useState(null);
  const enterTimerRef = useRef(null);
  const exitTimerRef = useRef(null);

  const ENTER_DELAY = 450;
  const EXIT_DELAY = 180;
  // When transitioning between adjacent items, use a shorter delay
  const SWITCH_DELAY = 120;

  const clearPreviewTimers = () => {
    clearTimeout(enterTimerRef.current);
    clearTimeout(exitTimerRef.current);
  };

  const closePreview = useCallback(() => {
    setPreviewRole(null);
    setPreviewAnchorEl(null);
  }, []);

  const handleItemMouseEnter = useCallback((event, role) => {
    clearPreviewTimers();
    if (isMobile) return;
    const el = event.currentTarget;
    const delay = previewRole ? SWITCH_DELAY : ENTER_DELAY;
    if (previewRole) closePreview();
    enterTimerRef.current = setTimeout(() => {
      setPreviewRole(role);
      setPreviewAnchorEl(el);
    }, delay);
  }, [isMobile, previewRole, closePreview]);

  const handleItemMouseLeave = useCallback(() => {
    clearTimeout(enterTimerRef.current);
    exitTimerRef.current = setTimeout(closePreview, EXIT_DELAY);
  }, [closePreview]);

  const handlePopperMouseEnter = useCallback(() => {
    clearTimeout(exitTimerRef.current);
  }, []);

  const handlePopperMouseLeave = useCallback(() => {
    exitTimerRef.current = setTimeout(closePreview, EXIT_DELAY);
  }, [closePreview]);

  const handleItemFocus = useCallback((event, role) => {
    if (isMobile) return;
    clearPreviewTimers();
    setPreviewRole(role);
    setPreviewAnchorEl(event.currentTarget);
  }, [isMobile]);

  const handleItemBlur = useCallback(() => {
    exitTimerRef.current = setTimeout(closePreview, EXIT_DELAY);
  }, [closePreview]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY_COLLAPSED, String(next));
      return next;
    });
  }, []);

  // Shared start-chat semantics (#401 option D) — also closes the mobile
  // overlay via ThreadContext.handleNewThread, so no explicit closeOverlay here.
  const handleAgentClick = useStartAgentChat();

  const handleMenuOpen = (event, role) => {
    event.preventDefault();
    event.stopPropagation();
    clearPreviewTimers();
    closePreview();
    setSelectedAgent(role);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedAgent(null);
  };

  const handleEditAgent = (role) => {
    navigate('/agents/builder/' + role.id);
    if (isMobile) closeOverlay();
  };

  const handleArchiveAgent = async (role) => {
    try {
      await postgresBackendService.archiveRol(role.id);
      refetch();
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoSnack({ open: true, role });
      undoTimerRef.current = setTimeout(() => setUndoSnack({ open: false, role: null }), 5000);
    } catch (err) {
      console.error('[SidebarAgentsList] Error archiving agent:', err);
      setErrorSnack({
        open: true,
        message: err?.message || t('sidebar.archiveError', 'No se pudo archivar el agente'),
      });
    }
  };

  const handleUndoArchive = async () => {
    const { role } = undoSnack;
    if (!role) return;
    try {
      await postgresBackendService.restoreRol(role.id);
      refetch();
    } catch (err) {
      console.error('[SidebarAgentsList] Error restoring agent:', err);
      setErrorSnack({
        open: true,
        message: err?.message || t('sidebar.restoreError', 'No se pudo restaurar el agente'),
      });
    }
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoSnack({ open: false, role: null });
  };

  const handleOpenCollections = (role) => {
    navigate('/editor/knowledge/' + role.id);
    if (isMobile) closeOverlay();
  };

  const handleNewAgent = () => {
    navigate('/agents/builder');
    if (isMobile) closeOverlay();
  };

  const agents = roles.filter((r) => !hiddenAgentIds.has(r.id));
  const hasMore = agents.length > DEFAULT_VISIBLE;
  const visibleAgents = showAll ? agents : agents.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = agents.length - DEFAULT_VISIBLE;
  const hiddenSidebarCount = roles.length - agents.length;

  return (
    <Box data-testid="sidebar-agents-list">
      {/* Section header — clickable to collapse */}
      <Box
        onClick={toggleCollapsed}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapsed(); } }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          ...hoverable({ bgcolor: 'action.hover' }),
          borderRadius: 1,
          mx: 0.5,
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            mr: 0.5,
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            color: 'text.disabled',
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: 0.5,
          }}
        >
          {t('sidebar.agentsSection', 'Agentes')}
          {isCollapsed && agents.length > 0 && (
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5, fontWeight: 400 }}>
              {agents.length}
            </Typography>
          )}
          {/* When the section is expanded and some agents are hidden from the
              sidebar, surface that count here. The "Gestionar agentes" inline
              link below used to host this signal but duplicated the top-nav
              "Agentes" entry that already routes to /explore. */}
          {!isCollapsed && hiddenSidebarCount > 0 && (
            <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
              {hiddenSidebarCount === 1
                ? t('sidebar.agentsHiddenCountOne', '· 1 oculto')
                : t('sidebar.agentsHiddenCountOther', '· {{count}} ocultos', { count: hiddenSidebarCount })}
            </Typography>
          )}
        </Typography>
        <Tooltip title={t('sidebar.newAgent', 'Nuevo agente')} placement="right" enterDelay={500}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleNewAgent(); }}
            aria-label={t('sidebar.newAgent', 'Nuevo agente')}
            sx={{
              color: 'text.secondary',
              p: 0.25,
              borderRadius: '4px',
              ...hoverable({ color: 'text.primary', bgcolor: 'action.selected' }),
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={!isCollapsed} timeout={200}>
        {/* Loading state */}
        {isLoading && (
          <Box sx={{ px: 2, py: 0.5 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={32} sx={{ mb: 0.25 }} />
            ))}
          </Box>
        )}

        {/* Agent list */}
        {!isLoading && agents.length > 0 && (
          <List disablePadding dense>
            {visibleAgents.map((role) => {
              const isHovered = hoveredAgentId === role.id;
              return (
                <ListItem
                  key={role.id}
                  disablePadding
                  onContextMenu={(e) => handleMenuOpen(e, role)}
                  onMouseEnter={(e) => { setHoveredAgentId(role.id); handleItemMouseEnter(e, role); }}
                  onMouseLeave={() => { setHoveredAgentId(null); handleItemMouseLeave(); }}
                  sx={{ position: 'relative' }}
                >
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, role)}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: 8,
                      transform: 'translateY(-50%)',
                      opacity: { xs: 1, md: isHovered ? 1 : 0 },
                      transition: 'opacity 0.2s ease-in-out',
                      bgcolor: 'background.paper',
                      ...hoverable({ bgcolor: 'action.hover' }),
                      minWidth: { xs: 44, md: 28 },
                      minHeight: { xs: 44, md: 28 },
                      zIndex: 1,
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <ListItemButton
                    onClick={() => handleAgentClick(role)}
                    onFocus={(e) => handleItemFocus(e, role)}
                    onBlur={handleItemBlur}
                    onKeyDown={(e) => { if (e.key === 'Escape') { clearPreviewTimers(); closePreview(); } }}
                    data-testid="agent-list-item"
                    aria-describedby={previewRole?.id === role.id ? 'agent-sidebar-popper' : undefined}
                    sx={{
                      minHeight: 30,
                      px: 1.5,
                      py: 0.125,
                      borderRadius: 1,
                      mx: 0.5,
                      paddingRight: '40px',
                      ...hoverable({ bgcolor: 'action.hover' }),
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: 'center' }}>
                      {(() => {
                        const rc = prepareRoleColorForChat(role.color, theme);
                        const draftTooltip = role.hasDraft
                          ? (() => {
                              const rel = formatRelativeDraft(role.draftUpdatedAt, t);
                              return rel
                                ? t('sidebar.agentDraftTooltip', 'Borrador sin publicar · {{relative}}', { relative: rel })
                                : t('sidebar.agentDraftTooltipNoTime', 'Borrador sin publicar');
                            })()
                          : '';
                        return (
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                background: rc.avatarBgGradient,
                                boxShadow: rc.avatarShadow,
                                color: rc.avatarText,
                              }}
                            >
                              {getInitials(role.name)}
                            </Avatar>
                            {role.hasDraft && (
                              <Tooltip title={draftTooltip} arrow placement="right">
                                <Box
                                  data-testid="sidebar-agent-draft-dot"
                                  aria-label={draftTooltip}
                                  sx={{
                                    position: 'absolute',
                                    top: -2,
                                    right: -2,
                                    width: 9,
                                    height: 9,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                    border: (th) => `1.5px solid ${th.palette.background.paper}`,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })()}
                    </ListItemIcon>
                    <AgentNameText name={role.name} />
                    {role.isNew && <NewAgentTag sx={{ ml: 0.5 }} />}
                    {role.isSystem && (
                      <Tooltip title={t('agents.systemTooltip', 'Agente del sistema — puedes personalizar instrucciones y objetivos')} arrow placement="right">
                        <ShieldOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, flexShrink: 0 }} />
                      </Tooltip>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Show more / show less toggle */}
        {!isLoading && hasMore && (
          <Typography
            variant="caption"
            component="button"
            onClick={() => setShowAll((prev) => !prev)}
            sx={{
              display: 'block',
              px: 2,
              py: 0.5,
              color: 'text.disabled',
              cursor: 'pointer',
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              ...hoverable({ color: 'primary.main' }),
            }}
          >
            {showAll
              ? t('sidebar.showLess', 'Mostrar menos')
              : t('sidebar.agentsShowMore', '{{count}} más...', { count: hiddenCount })}
          </Typography>
        )}

        {/* "Gestionar agentes" inline link removed — the top-nav "Agentes"
            entry (UnifiedSidebar) already routes to /explore. The "X oculto/s"
            hint that lived next to it moved to the section header above. */}

        {/* Hover preview popper — single instance, repositions per item */}
        <Popper
          id="agent-sidebar-popper"
          open={Boolean(previewRole && previewAnchorEl)}
          anchorEl={previewAnchorEl}
          placement="right-start"
          transition
          modifiers={[
            { name: 'offset', options: { offset: [0, 12] } },
            { name: 'flip', options: { fallbackPlacements: ['right-end', 'right', 'left-start'] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
          onMouseEnter={handlePopperMouseEnter}
          onMouseLeave={handlePopperMouseLeave}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <div>
                <AgentPreviewCard
                  role={previewRole}
                  isHidden={previewRole ? hiddenAgentIds.has(previewRole.id) : false}
                  onChat={() => { closePreview(); handleAgentClick(previewRole); }}
                  onEdit={() => { closePreview(); handleEditAgent(previewRole); }}
                />
              </div>
            </Fade>
          )}
        </Popper>

        {/* Empty state */}
        {!isLoading && agents.length === 0 && (
          <Typography
            variant="caption"
            component="button"
            onClick={handleNewAgent}
            sx={{
              display: 'block',
              px: 2,
              py: 0.5,
              color: 'text.disabled',
              cursor: 'pointer',
              border: 'none',
              bgcolor: 'transparent',
              textAlign: 'left',
              ...hoverable({ color: 'text.primary' }),
            }}
          >
            {t('sidebar.agentsEmpty', 'Crear tu primer agente')}
          </Typography>
        )}
      </Collapse>

      {/* Context menu */}
      <SidebarItemMenu
        anchorEl={menuAnchorEl}
        selectedItem={selectedAgent}
        onClose={handleMenuClose}
        onEdit={handleEditAgent}
        onDelete={handleArchiveAgent}
        onCollections={handleOpenCollections}
        onNewChat={handleAgentClick}
        onToggleHidden={(role) => toggleHidden(role.id)}
        isHidden={selectedAgent ? hiddenAgentIds.has(selectedAgent.id) : false}
        editLabel={t('sidebar.agentMenu.edit', 'Editar')}
        deleteLabel={t('sidebar.agentMenu.archive', 'Archivar')}
        collectionsLabel={t('sidebar.agentMenu.collections', 'Colecciones')}
        newChatLabel={t('sidebar.agentMenu.newChat', 'Iniciar un chat')}
        hideLabel={t('sidebar.agentMenu.hideFromSidebar', 'Ocultar del sidebar')}
        showLabel={t('sidebar.agentMenu.showInSidebar', 'Mostrar en sidebar')}
        skipConfirmation
        hideDelete={selectedAgent?.isLocked}
      />

      <Snackbar
        open={undoSnack.open}
        message={t('sidebar.agentArchived', 'Agente archivado')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Button color="primary" size="small" onClick={handleUndoArchive}>
            {t('common.undo', 'Deshacer')}
          </Button>
        }
      />

      <Snackbar
        open={errorSnack.open}
        autoHideDuration={6000}
        onClose={() => setErrorSnack({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setErrorSnack({ open: false, message: '' })}
          sx={{ width: '100%' }}
        >
          {errorSnack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SidebarAgentsList;
